import type { CSSProperties, ReactNode } from 'react';
import { COLORS } from './colors.ts';

// Re-export so all takeoff surfaces can grab palette + helpers + components
// from one path: `import { COLORS, mono, Card, ... } from '.../takeoff/primitives'`.
export { COLORS };

export function mono(size: number, color: string): CSSProperties {
  return { fontSize: size, color, fontFamily: "'JetBrains Mono', monospace" };
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: 14,
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
      }}
    >
      {children}
    </div>
  );
}

export interface MetaRowProps {
  label: string;
  value: string;
  warn?: boolean;
  ok?: boolean;
}

export function MetaRow({ label, value, warn, ok }: MetaRowProps) {
  const valueColor = ok ? COLORS.green : warn ? COLORS.red : COLORS.textMuted;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 6,
      }}
    >
      <span style={mono(10, COLORS.textDim)}>{label}</span>
      <span style={mono(12, valueColor)}>{value}</span>
    </div>
  );
}

export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export function SaveBadge({ status }: { status: SaveStatus }) {
  const map: Record<SaveStatus, { label: string; color: string }> = {
    idle: { label: '—', color: COLORS.textDim },
    dirty: { label: 'unsaved', color: COLORS.purple },
    saving: { label: 'saving…', color: COLORS.textDim },
    saved: { label: 'saved', color: COLORS.green },
    error: { label: 'save failed', color: COLORS.red },
  };
  const { label, color } = map[status];
  return <span style={mono(11, color)}>{label}</span>;
}

export function Loader({ text }: { text: string }) {
  return <div style={{ color: COLORS.textDim, fontSize: 13 }}>{text}</div>;
}

export function ErrorBox({ message }: { message: string }) {
  return (
    <div
      style={{
        color: COLORS.red,
        fontSize: 13,
        padding: 16,
        border: `1px solid ${COLORS.redBorder}`,
        borderRadius: 8,
      }}
    >
      {message}
    </div>
  );
}

export function NavBtn({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: `1px solid ${COLORS.border}`,
        color: disabled ? COLORS.textDim : COLORS.textMuted,
        borderRadius: 5,
        padding: '4px 10px',
        fontSize: 13,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {children}
    </button>
  );
}
